import type { AlphaRun } from "@nlc/shared-types";

import { ProgressBar } from "@/components/progress-bar";
import { cn } from "@/utils/cn";

import { useGenerationProgress } from "../hooks/useGenerationProgress";
import {
  ALPHA_BATCH_SIZE,
  type GenerationStep,
} from "./alphaGenerationProgress";
import { ALPHA_MOCKED_STYLES } from "./alphaMockedStyles";

const STEP_MARKERS: Record<GenerationStep["state"], string> = {
  active: "●",
  done: "✓",
  pending: "○",
};

const STEP_COLORS: Record<GenerationStep["state"], string> = {
  active: "text-text-brand-primary",
  done: "text-text-success-primary",
  pending: "text-text-quaternary",
};

function DesignBox({
  design,
}: {
  design: AlphaRun["designs"][number] | undefined;
}) {
  if (design?.imageUrl) {
    return (
      <img
        alt={`Concept ${design.planIndex + 1}`}
        className="aspect-square w-full rounded-lg object-cover"
        src={design.imageUrl}
      />
    );
  }

  const isFailed = design?.status === "failed";

  return (
    <div
      aria-label={isFailed ? "Concept failed" : undefined}
      className={cn(
        "aspect-square w-full rounded-lg border border-border-secondary bg-bg-secondary",
        !isFailed &&
          "bg-gradient-to-r from-bg-secondary via-bg-tertiary to-bg-secondary bg-[length:200%_100%] motion-safe:animate-shimmer",
      )}
      data-testid="design-box-empty"
    />
  );
}

function formatSize({ format }: AlphaRun): string {
  return format ? `${format.width} × ${format.height}` : "Chosen for you";
}

export function AlphaGenerationScreen({ run }: { run: AlphaRun }) {
  const { conceptNumber, percent, steps } = useGenerationProgress(run);
  const designsByPlanIndex = new Map(
    run.designs.map((design) => [design.planIndex, design]),
  );

  return (
    <div className="flex flex-col gap-3xl p-3xl lg:flex-row">
      <div className="flex min-w-0 flex-1 flex-col gap-3xl">
        <div className="flex flex-col gap-md">
          <h1 className="text-display-sm font-extrabold text-text-primary">
            Building your ads.
          </h1>
          <p className="text-lg text-text-tertiary">
            Stay on this page. Your first concepts show up as soon as
            they&apos;re ready.
          </p>
        </div>
        <div className="flex flex-col gap-md">
          <div className="flex items-center justify-between text-md font-bold">
            <span className="text-text-primary">
              Designing concept {conceptNumber} of {ALPHA_BATCH_SIZE}
            </span>
            <span className="text-text-brand-primary">{percent}%</span>
          </div>
          <ProgressBar
            fillColor="var(--color-bg-brand-solid)"
            showValue={false}
            value={percent}
          />
          <ul className="flex flex-wrap gap-x-lg gap-y-sm text-md font-semibold">
            {steps.map(({ label, state }) => (
              <li className={STEP_COLORS[state]} key={label}>
                {STEP_MARKERS[state]} {label}
              </li>
            ))}
          </ul>
        </div>
        <div className="grid grid-cols-5 gap-md">
          {Array.from({ length: ALPHA_BATCH_SIZE }, (_, planIndex) => (
            <DesignBox
              design={designsByPlanIndex.get(planIndex)}
              key={planIndex}
            />
          ))}
        </div>
      </div>
      <aside className="flex h-fit min-w-[330px] flex-col gap-lg rounded-xl border border-border-secondary bg-bg-secondary p-2xl lg:max-w-[380px]">
        <h2 className="text-md font-bold uppercase text-text-secondary">
          Your prompt
        </h2>
        <p className="text-md text-text-primary">{run.prompt}</p>
        <div className="flex flex-col gap-sm">
          <h3 className="text-sm font-bold text-text-tertiary">Styles</h3>
          <ul className="flex gap-sm">
            {ALPHA_MOCKED_STYLES.map((style) => (
              <li
                className="rounded-full bg-bg-brand-primary px-lg py-xs text-sm font-bold text-text-brand-primary"
                key={style}
              >
                {style}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col gap-sm">
          <h3 className="text-sm font-bold text-text-tertiary">Size</h3>
          <p className="text-md text-text-primary">{formatSize(run)}</p>
        </div>
      </aside>
    </div>
  );
}
